import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import OfferCard from "@/components/OfferCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchOffers } from "@/lib/api/offers";
import type { EnhancedOffer } from "@/lib/api/offers/types";

const Marketplace = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const { data: offers = [], isLoading, isError } = useQuery<EnhancedOffer[]>({
    queryKey: ['offers'],
    queryFn: fetchOffers,
    meta: {
      onSettled: (_, error) => {
        if (error) {
          toast({
            title: "Error",
            description: "Failed to load marketplace offers",
            variant: "destructive",
          });
        }
      }
    }
  });

  // Filter offers based on search term and filters
  let filteredOffers = offers.filter((offer) => {
    // Filter by search term (if any)
    const matchesSearch =
      !searchTerm ||
      offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      offer.seller_profile.name.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by price (if any)
    const matchesPrice =
      priceFilter === "all" ||
      !priceFilter ||
      (priceFilter === "low" && offer.price_cents < 15000) ||
      (priceFilter === "medium" &&
        offer.price_cents >= 15000 &&
        offer.price_cents <= 25000) ||
      (priceFilter === "high" && offer.price_cents > 25000);

    // Filter by category (if any)
    const matchesCategory =
      categoryFilter === "all" ||
      !categoryFilter ||
      offer.link_type === categoryFilter ||
      offer.tags.includes(categoryFilter);

    return matchesSearch && matchesPrice && matchesCategory;
  }) || [];

  return (
    <div className="bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row justify-between items-start mb-6 gap-4">
          <div className="w-full lg:w-1/3">
            <h1 className="text-3xl font-bold mb-2">Marketplace</h1>
            <p className="text-gray-600">
              Browse our curated selection of high-quality backlink opportunities
            </p>
          </div>
          
          <div className="w-full lg:w-2/3 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Search for sites, niches, or sellers..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <Select value={priceFilter} onValueChange={setPriceFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="low">Under $150</SelectItem>
                <SelectItem value="medium">$150 - $250</SelectItem>
                <SelectItem value="high">Over $250</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Guest Post">Guest Post</SelectItem>
                <SelectItem value="Niche Edit">Niche Edit</SelectItem>
                <SelectItem value="Newsletter">Newsletter</SelectItem>
                <SelectItem value="Featured Post">Featured Post</SelectItem>
                <SelectItem value="Tech">Tech</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
                <SelectItem value="Health">Health</SelectItem>
                <SelectItem value="Travel">Travel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="bg-white shadow rounded-lg p-3 h-48 animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="grid grid-cols-4 gap-1 mb-4">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-8 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-5 bg-gray-200 rounded w-full mt-2"></div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Failed to load marketplace offers
            </h3>
            <p className="text-gray-600 mb-4">
              There was an error loading the available offers.
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No offers found
            </h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your filters or search term to find more results.
            </p>
            <Button onClick={() => {
              setSearchTerm("");
              setPriceFilter("all");
              setCategoryFilter("all");
            }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredOffers.map((offer) => (
              <OfferCard 
                key={offer.id}
                id={offer.id}
                title={offer.title}
                price={offer.price_cents / 100}
                dr={offer.dr}
                da={offer.da}
                traffic={offer.traffic_estimate}
                linkType={offer.link_type}
                turnaroundDays={offer.turnaround_days}
                tags={offer.tags}
                sellerName={offer.seller_profile.name || "Unknown Seller"}
                sellerId={offer.seller_profile_id || ""}
                sellerRating={4.5}  // This would need to be fetched from reviews in a more complete implementation
                sellerReviewCount={10} // This would need to be fetched from reviews in a more complete implementation
                slug={offer.slug}
                sellerSlug={offer.seller_profile.slug}
              />
            ))}
          </div>
        )}
        
        <div className="mt-8 flex justify-center">
          <Button variant="outline" size="lg">
            Load More
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
